import { REPORT_WHO_SEPARATOR, type ReportPerson } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import { useId, useState } from "react";
import { dayParam } from "../conversations/conversation-search";
import { Button } from "../design/components/Button";
import { Checkbox } from "../design/components/Checkbox";
import { Segmented } from "../design/components/Segmented";
import { TextField } from "../design/components/TextField";
import { formatNumericDate } from "../i18n/format";
import { useT } from "../i18n/locale";
import { chosenOf, CUSTOM_PERIOD_MAX_DAYS, dayCountOf, DEFAULT_REPORT_PERIOD, periodDaysOf, REPORT_PERIOD_PARAMS, REPORT_PERIOD_ORDER, type ReportsSearch } from "./report-search";

// Periodo e persone del Report (piano M7, Task 3; mock-up `r-controls`, `readCustom`, `scopeText`, `rangeText`, `compareText`): i
// periodi di SEZ-3 con, accanto, i giorni del periodo («Dal 16/09/2026 al 15/10/2026, oggi fino alle 16:20»); «Personalizzato» con
// «Dal» e «Al», i cui errori si dicono sotto il campo che li ha e lasciano i numeri come sono (l'indirizzo cambia soltanto con due
// giorni giusti); «Di chi» soltanto per chi vede il team (SEZ-1), una casella per persona e «Tutto il team»; chi si sta guardando e,
// sotto, con che cosa confrontano le frecce. «Personalizzato» parte dai giorni che si stanno guardando.

/** Le persone della risposta e che cosa può vedere chi guarda: `own` i suoi numeri, `all` il team con «Di chi». */
export type ReportPeople = { scope: "own" | "all"; list: readonly ReportPerson[] };

type Props = {
  search: ReportsSearch;
  update: (change: Partial<ReportsSearch>) => void;
  /** Oggi nel fuso dell'azienda (`AAAA-MM-GG`). */
  today: string;
  /** Il periodo scritto, con la maiuscola. */
  rangeLabel: string;
  /** La frase del confronto. */
  compareLabel: string;
  /** Le persone, quando la prima risposta è arrivata. */
  people: ReportPeople | null;
  /** Nella «Guarda come…»: chi guarda vede i numeri della persona osservata. */
  watching: boolean;
};

export function ReportControls({ search, update, today, rangeLabel, compareLabel, people, watching }: Props) {
  const t = useT();
  const period = search.periodo ?? DEFAULT_REPORT_PERIOD;

  function choose(value: string) {
    const next = REPORT_PERIOD_ORDER.find((candidate) => candidate === value);
    if (next === undefined || next === period) return;
    if (next === "personalizzato") {
      const days = periodDaysOf(search, today);
      update({ periodo: next, dal: days.from, al: days.to });
    } else {
      update({ periodo: next === DEFAULT_REPORT_PERIOD ? undefined : next, dal: undefined, al: undefined });
    }
  }

  return (
    <section className="g-report-controls" aria-label={t("reports.controls.label")}>
      <div className="g-report-controls__row">
        <span className="g-report-label" aria-hidden="true">
          {t("reports.controls.period")}
        </span>
        <Segmented
          label={t("reports.controls.period")}
          options={REPORT_PERIOD_ORDER.map((id) => ({ value: id, label: t(`conversations.periods.${REPORT_PERIOD_PARAMS[id]}`) }))}
          value={period}
          onChange={choose}
        />
        <span className="g-report-range">{rangeLabel}</span>
      </div>
      {period === "personalizzato" && search.dal !== undefined && search.al !== undefined ? (
        <CustomDays dal={search.dal} al={search.al} today={today} onChoose={(days) => update({ periodo: "personalizzato", ...days })} />
      ) : null}
      {people?.scope === "all" ? <PeoplePicker people={people.list} chosen={chosenOf(search)} update={update} /> : null}
      {people ? <ScopeSentence people={people} chosen={people.scope === "all" ? chosenOf(search) : []} watching={watching} /> : null}
      <p className="g-report-compare">{compareLabel}</p>
    </section>
  );
}

type CustomProblem = { field: "from" | "to"; key: MessageKey };

/** Che cosa non va nei due giorni di «Personalizzato», nell'ordine del mock-up, e sotto quale campo dirlo; `null` se vanno. */
function customProblemOf(from: string, to: string, today: string): CustomProblem | null {
  if (dayParam(from) === undefined) return { field: "from", key: "reports.controls.customMissing" };
  if (dayParam(to) === undefined) return { field: "to", key: "reports.controls.customMissing" };
  if (from > to) return { field: "from", key: "reports.controls.customOrder" };
  if (to > today) return { field: "to", key: "reports.controls.customFuture" };
  if (dayCountOf(from, to) > CUSTOM_PERIOD_MAX_DAYS) return { field: "from", key: "reports.controls.customTooLong" };
  return null;
}

type CustomProps = { dal: string; al: string; today: string; onChoose: (days: { dal: string; al: string }) => void };

/**
 * «Dal» e «Al»: una bozza dei due campi, che va nell'indirizzo soltanto quando i due giorni vanno bene. Se l'indirizzo cambia da fuori
 * (il ritorno con il browser), la bozza riparte da lui: si ricorda da quale indirizzo è nata.
 */
function CustomDays({ dal, al, today, onChoose }: CustomProps) {
  const t = useT();
  const baseId = useId();
  const source = `${dal}|${al}`;
  const [draft, setDraft] = useState({ from: dal, to: al, source });
  let current = draft;
  if (draft.source !== source) {
    current = { from: dal, to: al, source };
    setDraft(current);
  }
  const problem = customProblemOf(current.from, current.to, today);
  const message = problem === null ? undefined : t(problem.key, { day: formatNumericDate("UTC", today) });

  function change(next: Partial<{ from: string; to: string }>) {
    const value = { ...current, ...next };
    setDraft(value);
    if (customProblemOf(value.from, value.to, today) === null) onChoose({ dal: value.from, al: value.to });
  }

  return (
    <div className="g-report-custom">
      <TextField
        id={`${baseId}-from`}
        label={t("conversations.fromDay")}
        type="date"
        min="1900-01-01"
        max={today}
        value={current.from}
        error={problem?.field === "from" ? message : undefined}
        onChange={(event) => change({ from: event.target.value })}
      />
      <TextField
        id={`${baseId}-to`}
        label={t("conversations.toDay")}
        type="date"
        min="1900-01-01"
        max={today}
        value={current.to}
        error={problem?.field === "to" ? message : undefined}
        onChange={(event) => change({ to: event.target.value })}
      />
    </div>
  );
}

type PickerProps = { people: readonly ReportPerson[]; chosen: readonly string[]; update: (change: { chi?: string | undefined }) => void };

/**
 * «Di chi»: una casella per persona, nell'ordine alfabetico del server, e «Tutto il team», che toglie le scelte. La usa anche
 * «Attività» (SEZ-8), con le stesse parole e lo stesso `chi` nell'indirizzo.
 */
export function PeoplePicker({ people, chosen, update }: PickerProps) {
  const t = useT();
  const baseId = useId();

  function toggle(id: string, checked: boolean) {
    const listed = people.map((person) => person.membershipId).filter((candidate) => (candidate === id ? checked : chosen.includes(candidate)));
    // Una persona dell'indirizzo che non è fra quelle del periodo resta scelta.
    const others = chosen.filter((candidate) => candidate !== id && !people.some((person) => person.membershipId === candidate));
    const next = [...listed, ...others];
    update({ chi: next.length > 0 ? next.join(REPORT_WHO_SEPARATOR) : undefined });
  }

  return (
    <fieldset className="g-report-people">
      <legend>{t("reports.controls.who")}</legend>
      <div className="g-report-people__list">
        {people.map((person) => (
          <Checkbox
            key={person.membershipId}
            id={`${baseId}-${person.membershipId}`}
            className="g-report-person"
            label={person.displayName}
            {...(person.active ? {} : { hint: t("reports.controls.inactive") })}
            checked={chosen.includes(person.membershipId)}
            onChange={(checked) => toggle(person.membershipId, checked)}
          />
        ))}
        <Button variant="ghost" aria-pressed={chosen.length === 0} onClick={() => update({ chi: undefined })}>
          {t("reports.controls.wholeTeam")}
        </Button>
      </div>
      <p className="g-report-people__hint">{t("reports.controls.whoHint")}</p>
    </fieldset>
  );
}

/** Chi si sta guardando (`scopeText` del mock-up): la prima parte in grassetto. */
function ScopeSentence({ people, chosen, watching }: { people: ReportPeople; chosen: readonly string[]; watching: boolean }) {
  const t = useT();
  if (people.scope === "own") {
    const name = people.list[0]?.displayName ?? "";
    // Nella «Guarda come…» la frase nomina la persona osservata, senza «lei» o «lui».
    return (
      <p className="g-report-scope">
        <strong>{watching ? t("reports.scope.watchingLead", { name }) : name}</strong>
        {watching ? t("reports.scope.watching") : t("reports.scope.own")}
      </p>
    );
  }
  if (chosen.length === 0) {
    return (
      <p className="g-report-scope">
        <strong>{t("reports.scope.team")}</strong>
        {people.list.length === 1 ? t("reports.scope.teamCountOne") : t("reports.scope.teamCount", { count: people.list.length })}
      </p>
    );
  }
  const names = people.list.filter((person) => chosen.includes(person.membershipId)).map((person) => person.displayName);
  const unknown = chosen.filter((id) => !people.list.some((person) => person.membershipId === id));
  return (
    <p className="g-report-scope">
      <strong>{chosen.length === 1 ? t("reports.scope.one") : t("reports.scope.many", { count: chosen.length })}</strong>
      {t("reports.scope.chosen", { names: [...names, ...unknown].join(", ") })}
    </p>
  );
}
